import React, { useContext, useState, useEffect, useRef } from "react";
import {
    StandardModal,
    StandardModalActionButton,
    StandardModalActionGroup,
    Toast
} from "src/components";
import {
    SecurePassphraseStorage,
    backupPasswordIsIncorrect,
    createChatBackupWithPassphrase,
    restoreChatBackupWithPassphrase
} from "../../utils";
import {
    BACKUP_KEY_FORMS,
} from "src/scenes/Chat/hooks/chat/useBackupForm";
import {
    BACKUP_STORAGE_KEYS,
    getBackupStorageValue,
    removeBackupStorageValue,
    setBackupStorageValue,
} from "src/scenes/Chat/utils/matrixBackupStorage";
import { ChatContext } from "src/scenes/Chat/ChatContext";
import { BackupKeyContext } from "src/scenes/Chat/BackupKeyContext";
import { UserContext } from "src/scenes/App/UserContext";
import { clearMatrixRecoveryPending } from "src/scenes/Chat/utils/matrixStorage";
import Create from "./Create";
import Restore from "./Restore";
import Reset from "./Reset";

const BackupKeyModal = ({ showDialog }) => {
    const isMounted = useRef(true);
    const { matrixClient } = useContext(ChatContext);
    const userContext = useContext(UserContext);
    const { backupFormToShow, closeBackupKeyForm, setUserHasBackupKeys } = useContext(BackupKeyContext);

    const [isProcessingBackup, setIsProcessingBackup] = useState(false);
    const backupFormRef = useRef(null);

    const handleCreateBackup = async (passphrase) => {
        if (!isMounted.current) return;
        setIsProcessingBackup(true);

        try {
            await createChatBackupWithPassphrase(matrixClient, passphrase);
            setUserHasBackupKeys(true);
            clearMatrixRecoveryPending(userContext.uuid);
            closeBackupKeyForm(false);
            Toast.success("Backup created. Please save your recovery password.");
        } catch (err) {
            console.error("Error creating passphrase backup:", err);
            Toast.error("Failed to create backup.");
        } finally {
            if (isMounted.current) setIsProcessingBackup(false);
        }
    };

    const handleRestoration = async (passphrase) => {
        if (!isMounted.current) return;
        setIsProcessingBackup(true);
        try {
            const crypto = matrixClient.getCrypto();
            if (!crypto) throw new Error("Crypto not available");

            const backupInfo = await crypto.getKeyBackupInfo();
            if (!backupInfo) throw new Error("No key backup found.");

            const result = await restoreChatBackupWithPassphrase(matrixClient, passphrase, backupInfo);

            console.warn("Backup restored :", result);
            clearMatrixRecoveryPending(userContext.uuid);
            Toast.success("Backup restored successfully!");
            setTimeout(() => {
                setBackupStorageValue(BACKUP_STORAGE_KEYS.USER_KEY_RESTORED, matrixClient);
                window.location.reload();
            }, 1000);
        } catch (error) {
            console.error("Restoration error:", error);
            let message = "Cannot restore chat history.";
            if (backupPasswordIsIncorrect(error)) {
                message = "Incorrect backup key password provided.";
            }
            SecurePassphraseStorage.clear(matrixClient);
            Toast.error(message);
            if (getBackupStorageValue(BACKUP_STORAGE_KEYS.USER_KEY_RESTORED, matrixClient)) {
                removeBackupStorageValue(BACKUP_STORAGE_KEYS.USER_KEY_RESTORED, matrixClient);
            }
        } finally {
            if (isMounted.current) setIsProcessingBackup(false);
        }
    };

    useEffect(() => {
        isMounted.current = true;
        // Only run when modal is open and matrixClient available
        if (showDialog && matrixClient) {
            const cryptoApi = matrixClient.getCrypto();
            if (cryptoApi) {
                cryptoApi.checkKeyBackupAndEnable().catch((err) => {
                    if (isMounted.current) {
                        console.error("Failed to check or enable key backup:", err);
                    }
                });
            }
        }
        return () => {
            isMounted.current = false;
        };
    }, [showDialog, matrixClient]);

    return (
        <StandardModal
            open={showDialog}
            onClose={closeBackupKeyForm}
            title="Chat Backup"
            widthMode="compactWide"
            footer={(
                <StandardModalActionGroup $pushRight>
                    <StandardModalActionButton onClick={closeBackupKeyForm}>Back</StandardModalActionButton>
                    <StandardModalActionButton
                        $variant="primary"
                        disabled={isProcessingBackup}
                        onClick={() => backupFormRef.current?.submit()}
                    >
                        Confirm
                    </StandardModalActionButton>
                </StandardModalActionGroup>
            )}
        >
            {backupFormToShow === BACKUP_KEY_FORMS.CREATE && (
                <Create
                    ref={backupFormRef}
                    disableButtons={isProcessingBackup}
                    handleBackupKeyPasswordCreation={handleCreateBackup}
                />
            )}

            {backupFormToShow === BACKUP_KEY_FORMS.RESTORE && (
                <Restore
                    ref={backupFormRef}
                    disableButtons={isProcessingBackup}
                    handleRestoration={handleRestoration}
                />
            )}

            {backupFormToShow === BACKUP_KEY_FORMS.RESET && (
                <Reset
                    ref={backupFormRef}
                    disableButtons={isProcessingBackup}
                    handleBackupKeyPasswordCreation={handleCreateBackup}
                />
            )}
        </StandardModal>
    );
};

export default BackupKeyModal;
